import * as tslib_1 from "tslib";
/**
 * @file Abstract base class for configurable controls and views.
 * @author Tom Verschoore de la Houssaije
 */
import { AbstractBase } from '../../core/helpers/abstract-base';
import { OnInit } from '@angular/core';
import { DataRights } from '../helpers/data-rights.enum';
/**
 * Abstract base class for configurable controls and views.
 */
var AbstractComponent = /** @class */ (function (_super) {
    tslib_1.__extends(AbstractComponent, _super);
    /**
     * Creates an instance of AbstractComponent.
     * @param {AlertsService} alertsService The service is used to alert about missing configuration.
     * @memberOf AbstractComponent
     */
    function AbstractComponent(alertsService) {
        var _this = _super.call(this) || this;
        _this.alertsService = alertsService;
        /**
         * The component configuration.
         * @protected
         * @type {ComponentConfiguration}
         * @memberOf AbstractComponent
         */
        _this._configuration = undefined;
        /**
         * Reference for holding data rights from the configuration.
         * @protected
         * @type {DataRights}
         * @memberOf AbstractComponent
         */
        _this.rights = DataRights.Read;
        _this.rights = _this.defaultRights();
        return _this;
    }
    Object.defineProperty(AbstractComponent.prototype, "configuration", {
        /** The configuration of the view. */
        /**
         * Gets or sets the configuration of this component.
         * @readonly
         * @type {ComponentConfiguration}
         * @memberOf AbstractComponent
         */
        get: function () {
            return this._configuration;
        },
        set: function (value) {
            var _this = this;
            if (this._configuration === value) {
                return;
            }
            this._configuration = value;
            if (this._configuration) {
                if (!this._configuration.rights) {
                    this._configuration.rights = this.defaultRights();
                }
                window.setTimeout(function () {
                    // Fix issue: #2553
                    _this.rights = _this._configuration.rights;
                });
                this.afterInitialize();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AbstractComponent.prototype, "canCreate", {
        /**
         * Gets the create rights of the component.
         * @readonly
         * @type {boolean}
         * @memberOf AbstractComponent
         */
        get: function () {
            // tslint:disable-next-line:no-bitwise
            return DataRights.Create === (this.rights & DataRights.Create);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AbstractComponent.prototype, "canRead", {
        /**
         * Gets the read rights of the component.
         * @readonly
         * @type {boolean}
         * @memberOf AbstractComponent
         */
        get: function () {
            // tslint:disable-next-line:no-bitwise
            return DataRights.Read === (this.rights & DataRights.Read);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AbstractComponent.prototype, "canUpdate", {
        /**
         * Gets the update rights of the component.
         * @readonly
         * @type {boolean}
         * @memberOf AbstractComponent
         */
        get: function () {
            // tslint:disable-next-line:no-bitwise
            return DataRights.Update === (this.rights & DataRights.Update);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AbstractComponent.prototype, "canDelete", {
        /**
         * Gets the delete rights of the component.
         * @readonly
         * @type {boolean}
         * @memberOf AbstractComponent
         */
        get: function () {
            return this.getCanDelete();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Sends a warning when the configuration is missing.
     * @memberOf AbstractComponent
     */
    AbstractComponent.prototype.ngOnInit = function () {
        if (!this.configuration) {
            this.alertsService.createAlert('danger', 'APP.CORE.CONFIGURATION_MISSING', this.constructor.name);
        }
    };
    /**
     * Gets the delete rights of the component.
     * Use this method to override default behavior.
     * @protected
     * @returns {boolean} True when displayed entity can be deleted.
     * @memberOf AbstractComponent
     */
    AbstractComponent.prototype.getCanDelete = function () {
        // tslint:disable-next-line:no-bitwise
        return DataRights.Delete === (this.rights & DataRights.Delete);
    };
    /**
     * Returns the default CRUD rights of this component.
     * @protected
     * @returns {DataRights}
     * @memberOf AbstractComponent
     */
    AbstractComponent.prototype.defaultRights = function () {
        // tslint:disable-next-line:no-bitwise
        return DataRights.Create | DataRights.Read | DataRights.Update | DataRights.Delete;
    };
    /**
     * Finds a match when the given name is part of the configuration key of a sub component.
     * @param name The name to match.
     * @returns The found configuration key otherwise undefined.
     * @memberOf AbstractComponent
     */
    AbstractComponent.prototype.findChildConfiguration = function (name) {
        var found = this.findChildComponent(name);
        if (!found) {
            return undefined;
        }
        return found.configuration;
    };
    /**
     * Finds a match when the given name is part of the configuration key of a sub component.
     * @param name The name to match.
     * @returns The found ChildComponent key otherwise undefined.
     * @memberOf AbstractComponent
     */
    AbstractComponent.prototype.findChildComponent = function (name) {
        if (!this.configuration) {
            return undefined;
        }
        if (!this.configuration.components) {
            return undefined;
        }
        var value = name.toLowerCase();
        var match = this.configuration.components.find(function (item) {
            return item.configuration.key.toLowerCase().indexOf(value) !== -1;
        });
        if (match) {
            return match;
        }
        return undefined;
    };
    /**
     * Is called after the block name is set and the configuration is loaded.
     * Override this method to process the loaded configuration data.
     * @protected
     * @memberOf AbstractComponent
     */
    AbstractComponent.prototype.afterInitialize = function () {
        // Place to implement specific code in extensions.
    };
    return AbstractComponent;
}(AbstractBase));
export { AbstractComponent };
